import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  Grid,
  TextField,
  Typography,
  MenuItem,
  Select,
  SelectChangeEvent,
  FormControl,
  InputLabel
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { createPrimaryGroup, updatePrimaryGroup, fetchGroupCategories, fetchNextSequencePreview } from '../../store/coaStore/coaThunk';
import { ApiStatus } from '../../utils/api.types';
import { IPrimaryGroup } from '../../utils/coa.types';
import { toast } from 'react-toastify';

interface PrimaryGroupFormProps {
  open: boolean;
  onClose: (refresh?: boolean) => void;
  editData?: IPrimaryGroup | null;
}

const schema = yup.object().shape({
  name: yup.string().required('Name is required').min(2, 'Name must be at least 2 characters').max(100, 'Name must not exceed 100 characters'),
  groupCategory: yup.string().required('Group Category is required'),
  description: yup.string().max(500, 'Description must not exceed 500 characters'),
});

const PrimaryGroupForm: FC<PrimaryGroupFormProps> = ({ open, onClose, editData }) => {
  const dispatch = useAppDispatch();
  const { groupCategories, status } = useAppSelector(state => state.coa);
  const [selectedGroupCategory, setSelectedGroupCategory] = useState('');
  const [previewAccountingCode, setPreviewAccountingCode] = useState('Auto');

  const methods = useForm({
    mode: 'onTouched',
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      groupCategory: '',
      description: '',
    }
  });

  const { handleSubmit, reset, formState: { errors } } = methods;

  useEffect(() => {
    if (open) {
      dispatch(fetchGroupCategories({ limit: 100 }));
    }
  }, [dispatch, open]);

  // Populate form when editing
  useEffect(() => {
    if (editData) {
      const categoryId = typeof editData.groupCategory === 'object'
        ? (editData.groupCategory as any)?._id
        : editData.groupCategory;

      reset({
        name: editData.name || '',
        description: editData.description || editData.descirption || '',
        groupCategory: categoryId || '',
      });
      setSelectedGroupCategory(categoryId || '');
      setPreviewAccountingCode(editData.accountingCode || 'Auto');
    } else {
      reset({
        name: '',
        groupCategory: '',
        description: '',
      });
      setSelectedGroupCategory('');
      setPreviewAccountingCode('Auto');
    }
  }, [editData, reset, open]);

  // Fetch next sequence when group category changes (only for new entries)
  useEffect(() => {
    if (!editData && selectedGroupCategory) {
      dispatch(fetchNextSequencePreview(selectedGroupCategory))
        .unwrap()
        .then((result) => {
          setPreviewAccountingCode(result.accountingCode);
        })
        .catch(() => {
          setPreviewAccountingCode('Auto');
        });
    }
  }, [selectedGroupCategory, editData, dispatch]);

  const onSubmit = async (data: any) => {
    try {
      if (editData?._id) {
        await dispatch(updatePrimaryGroup({ id: editData._id, payload: data })).unwrap();
      } else {
        await dispatch(createPrimaryGroup(data)).unwrap();
      }
      reset();
      onClose(true);
    } catch (error: any) {
      toast.error(error?.message || 'Something went wrong');
    }
  };

  const handleClose = () => {
    reset();
    setSelectedGroupCategory('');
    setPreviewAccountingCode('Auto');
    onClose(false);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 550 },
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}
        >
          {/* Header */}
          <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {editData ? 'Edit Primary Group' : 'Add Primary Group'}
            </Typography>
          </Box>

          {/* Content - Scrollable */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Controller
                  name="groupCategory"
                  control={methods.control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.groupCategory} size="small">
                      <InputLabel>Group Category *</InputLabel>
                      <Select
                        {...field}
                        label="Group Category *"
                        value={selectedGroupCategory}
                        onChange={(e: SelectChangeEvent) => {
                          setSelectedGroupCategory(e.target.value);
                          field.onChange(e.target.value);
                        }}
                      >
                        {groupCategories.map((cat) => (
                          <MenuItem key={cat._id} value={cat._id}>
                            {cat.name} {cat.accountingCode ? `(${cat.accountingCode})` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.groupCategory && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.groupCategory.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Name"
                      fullWidth
                      required
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      placeholder="e.g., Current Assets, Fixed Assets, Direct Expenses"
                      size="small"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      fullWidth
                      multiline
                      rows={3}
                      error={!!errors.description}
                      helperText={errors.description?.message}
                      placeholder="Optional description for this primary group"
                      size="small"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(0, 0, 0, 0.6)',
                    fontStyle: 'italic'
                  }}
                >
                  Sequence number and accounting code will be auto-generated upon saving.
                </Typography>
                {previewAccountingCode !== 'Auto' && (
                  <Typography
                    sx={{
                      color: 'rgba(0, 0, 0, 0.87)',
                      fontWeight: 600,
                      mt: 0.5,
                      fontSize: '1rem'
                    }}
                  >
                    Accounting Code: {previewAccountingCode}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>

          {/* Footer - Fixed at bottom */}
          <Box sx={{
            borderTop: '1px solid #e5e7eb',
            p: 2,
            backgroundColor: '#f9fafb',
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end'
          }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={status === ApiStatus.LOADING}
              sx={{
                minWidth: 100,
                height: 40,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={status === ApiStatus.LOADING}
              sx={{
                minWidth: 140,
                height: 40,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {editData ? 'Update' : 'Save'}
            </LoadingButton>
          </Box>
        </Box>
      </FormProvider>
    </Drawer>
  );
};

export default PrimaryGroupForm;
